import React from 'react';
import { connect } from 'react-redux';
import { mapProps, pure, withHandlers, withProps } from 'recompose';
import { compose, omit, path, pick, prop } from 'ramda';
import { useDeviceSelector, useUserSelector, useGameConfigurationSelector } from '@core/js/store/hooks';
import { withTranslations } from '@core/js/components/hocs/withTranslations';
import { Image } from '@core/js/components/common/Image';
import FeatureIcons from '@core/js/components/common/Game/FeatureIcons';
import { FavoriteIcon } from '@core/js/components/common/Game/FavoriteIcon';
import withGameLaunch from '@core/js/components/common/Game/withGameLaunch';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { useGetTrackLaunchGameFromBI } from '@core/js/components/main/Games/BICategories';

import { isGameAvailable } from '@core/js/components/common/Game/Game';
import { useLiveGameConfig, useGameImageUrls } from '@core/js/hooks/games';
import { AdditionalAssetsValues, LiveDataTypes } from './enum';

import style from './Game.scss';
import LiveStreamingVideo from './components/LiveStreamVideo';
import LiveDataContainer from './components/LiveDataContainer';
import { CategoryModificators } from '@theme/js/components/main/Games/Category/enum';
import { WebContent } from '@core/js/components/system/WebContent';

// FIXED: retest
// COD-118 [Desktop] Redesign game grid hover effect
// COD-214 [Mobile and Desktop] Move jackpots badge to the bottom of a tile
// COD-256 [Mobile] Wrong number of game tiles in a row
// COD-460 [Games grid/responsive games] Remove game info pop up from the games
// COD-584 Add support for the 4 badges for game tiles, both game portlets
export const GameBare = ({
        t,
        code,
        name,
        size = 'extraSmall',
        sizes,
        showTitle = false,
        showFeatureIcons = true,
        showFavoriteIcon,
        showRecentlyPlayedIcon,
        className = '',
        setGameRef,
        setGameImageRef,
        onClick,
        onMouseMove,
        isGameNew,
        isGameHot,
        isGamePopular,
        isGameExclusive,
        additionalAssets,
        ratingItem,
        includeLiveGames = false,
        liveLaunchAlias,
        categoryModificators = [],
        isRoundedCategory,
        isBannerCategory
}) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const {
		isScheduled,
		openingDateTime,
		isUnderMaintenance,
        launchAlias
    } = liveGameConfig;
    const liveGameType = additionalAssets?.find(asset => asset.name === AdditionalAssetsValues.LIVE_DATA_TYPE)?.url;
    const isLiveGame = includeLiveGames && launchAlias
        && Object.values(LiveDataTypes).includes(liveGameType)
        && categoryModificators.includes(CategoryModificators.LiveDataCategory);
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const liveStreamingData = additionalAssets?.find(asset => asset.name === AdditionalAssetsValues.HLS);

    const imageUrls = useGameImageUrls({
        code,
        sizes,
        isRoundedCategory
    });

    const isGameWcReplacer = categoryModificators.includes(CategoryModificators.GameWcReplacer);
    const GameWcReplacerId = additionalAssets?.find(asset => asset.name === AdditionalAssetsValues.WC_REPLACER_ID)?.url;
    const topCategoryClass = categoryModificators.find(mod => mod.startsWith(CategoryModificators.TopTenCategory));
    const topGamesAmount = topCategoryClass?.split('-')?.pop() || 0;

    if (isGameWcReplacer && GameWcReplacerId) {
        return <WebContent id={GameWcReplacerId} className={style.gameWC} />;
    }

    return (
        <AccessibilityElement
            onClick={onClick}
            data-game-code={code}
            elementRef={setGameRef}
            className={`${style.container} ${style[size]} ${className} ${liveStreamingData ? 'live-streaming' : ''}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
        >
            <div
                aria-hidden={true}
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={style.imageContainer}
            >
                {liveStreamingData || isUnderMaintenance
                    ? <LiveStreamingVideo
						imageUrl={imageUrls}
						asset={liveStreamingData}
						gameName={name}
						isUnderMaintenance={isUnderMaintenance}
					/>
                    : <Image alt={name} src={imageUrls} className={style.image} />
                }
                {isScheduledLiveGame && <div className={style.scheduledLiveGameBackground} />}
                {(showFeatureIcons || showFavoriteIcon) && <div className={style.background} />}
                { showRecentlyPlayedIcon && !isBannerCategory &&
                    <div className={style.recentlyPlayedIcon}>
                        <div className={style.gameName}>{name}</div>
                    </div>
                }
            </div>
            {showFeatureIcons && (
                <div className={style.labels}>
                    { isGameExclusive && <span className="gamesinfo__flag gamesinfo__flag-exclusive" /> }
                    { isGamePopular && <span className="gamesinfo__flag gamesinfo__flag-popular" /> }
                    { isGameNew && <span className="gamesinfo__flag gamesinfo__flag-new" /> }
                    { isGameHot && <span className="gamesinfo__flag gamesinfo__flag-hot" /> }
                </div>
            )}
            {
                topCategoryClass && ratingItem <= topGamesAmount && (
                    <div className={style.ranking}>
                        {ratingItem}
                        <div className={style.rankingAmount}>TOP {topGamesAmount}</div>
                    </div>
                )
            }
            {isScheduledLiveGame && (
                <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {openingDateTime
                        ? (
                            <>
                                <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                <span>{openingDateTime}</span>
                            </>
                        )
                        : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                    }
                </div>
            )}
            <div className={style.sideIcons}>
                {!showTitle && showFavoriteIcon && !isLiveGame && (
                    <FavoriteIcon
                        code={code}
                        className={style.favorite}
                    />
                )}
                <div className={style.spacer} />
                {showFeatureIcons && (
                    <FeatureIcons
                        code={code}
                        types={['goldenChips', 'wilds', 'fsb']}
                        classNames={{ item: style.featured }}
                        noContainer={true}
                    />
                )}
            </div>
            {showTitle && <div className={style.titleWrapper}>
                <span className={`${style.name} ${style.ellipsis}`}>{name}</span>
                {showFavoriteIcon && <FavoriteIcon code={code}
                    isFeatured={true}
                    className={style.favorite} />
                }
            </div>}
            {
                isLiveGame && <LiveDataContainer liveLaunchAlias={liveLaunchAlias} liveType={liveGameType} sizes={sizes} />
            }
        </AccessibilityElement>
    );
};

export { isGameAvailable };

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    withGameLaunch,
    withProps(({ code }) => ({
        trackLaunchGameFromBI: useGetTrackLaunchGameFromBI(),
        isDesktop: useDeviceSelector(prop('isDesktop')),
        ...useGameConfigurationSelector(path(['games', code])),
        ...useUserSelector(pick(['isLoggedIn']))
    })),
    withHandlers(({ launchGame, code, trackLaunchGameFromBI }) => ({
        onClick: () => (event) => {
            event.preventDefault();
            launchGame();
            trackLaunchGameFromBI(code);
        }
    })),
    mapProps(omit(['description', 'funSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);

export const withHOCs = compose(
    pure,
    withGameLaunch,
    withProps(() => ({
        trackLaunchGameFromBI: useGetTrackLaunchGameFromBI()
    })),
    withHandlers(({ launchGame, code, trackLaunchGameFromBI }) => ({
        onClick: () => {
            launchGame();
            trackLaunchGameFromBI(code);
        }
    })),
    connect((state, { code }) => ({
        ...state.app.games[code],
        isLoggedIn: state.user.isLoggedIn
    })),
    mapProps(omit(['description', 'funSupport', 'jackpotCode']))
);

export default withHOCs(Game);
